import { includes, values } from 'lodash/fp';
import { push } from 'connected-next-router';
import constants from '@ahmdigital/constants';
import getIncomeTypeFromScale from '@ahmdigital/logic/lib/utils/get-income-type-from-scale';
import moment from 'moment';

import {
  ADDRESS_CHANGE,
  CONTINUOUS_COVER_CHANGE,
  DATE_OF_BIRTH_CHANGE,
  FREQUENCY_CHANGE,
  HAS_REFINED_PRICE,
  HAS_SET_BASIC_DETAILS_CHANGE,
  HOSPITAL_CHANGE,
  HOSPITAL_EXCESS_CHANGE,
  INCOME_CHANGE,
  INCOME_TIER_CHANGE,
  INCOME_TYPE_CHANGE,
  LAST_VIEWED_PRODUCT_CHANGE,
  PAYMENT_METHOD_CHANGE,
  PRODUCT_CHANGE,
  PRODUCT_TYPE_CHANGE,
  REBATE_CHANGE,
  SCALE_CHANGE,
  STATE_CHANGE,
  SWITCHING_CHANGE,
} from '../action-types';
import { URLS } from '../../models/url/constants';
import ProductService from '../../models/product';
import type { Customer, CustomerMember } from '../../selectors/select-customer/types';
import type { CustomerProductType } from '../../types/state/customer';

export type StateValues = (typeof constants.STATE)[keyof typeof constants.STATE];

type Address = {
  residentialAddressState: StateValues;
};

function changeAddress(address: Address) {
  return {
    address,
    type: ADDRESS_CHANGE,
  };
}

function changeContinuousCover(
  continuous: CustomerMember['continuous'],
  memberRelationship: CustomerMember['relationship'],
) {
  return {
    continuous,
    relationship: memberRelationship,
    type: CONTINUOUS_COVER_CHANGE,
  };
}

function changeDateOfBirth(dateOfBirth: CustomerMember['dob'], memberRelationship: CustomerMember['relationship']) {
  return {
    dateOfBirth: moment(dateOfBirth, constants.DATE_FORMAT.STANDARD, true),
    relationship: memberRelationship,
    type: DATE_OF_BIRTH_CHANGE,
  };
}

function changeFrequency(frequency: Customer['frequency']) {
  return {
    frequency,
    type: FREQUENCY_CHANGE,
  };
}

function changeHasRefinedPrice(hasRefinedPrice: Customer['hasRefinedPrice']) {
  return {
    hasRefinedPrice,
    type: HAS_REFINED_PRICE,
  };
}

function changeHasSetBasicDetails(hasSetBasicDetails: Customer['hasSetBasicDetails']) {
  return {
    hasSetBasicDetails,
    type: HAS_SET_BASIC_DETAILS_CHANGE,
  };
}

function changeHospitalExcess(hospitalExcess: Customer['hospitalExcess']) {
  return (dispatch: any, getState: any) => {
    const products = getState()?.product?.data;

    dispatch({
      hospitalExcess,
      products,
      type: HOSPITAL_EXCESS_CHANGE,
    });
  };
}

function changeIncome(income: Customer['income']) {
  return {
    income,
    type: INCOME_CHANGE,
  };
}

function changeIncomeTier(incomeTier: Customer['incomeTier']) {
  return {
    incomeTier,
    type: INCOME_TIER_CHANGE,
  };
}

function changeIncomeType(incomeType: Customer['incomeType']) {
  return {
    incomeType,
    type: INCOME_TYPE_CHANGE,
  };
}

function changePaymentMethod(method: Customer['paymentMethod']) {
  return {
    method,
    type: PAYMENT_METHOD_CHANGE,
  };
}

function changeRebate(rebate: Customer['rebate']) {
  return {
    rebate,
    type: REBATE_CHANGE,
  };
}

function changeScale(scale: Customer['scale']) {
  return (dispatch: any, getState: any) => {
    const cartProduct = getState()?.cart?.product;

    if (cartProduct.type === constants.PRODUCT_TYPE.COMBINED && !ProductService.isAvailableFor(cartProduct, scale)) {
      dispatch({
        product: null,
        type: HOSPITAL_CHANGE,
      });
    }

    if (
      includes(cartProduct.type, [constants.PRODUCT_TYPE.PACKAGE, constants.PRODUCT_TYPE.HOSPITAL]) &&
      !ProductService.isAvailableFor(cartProduct, scale)
    ) {
      dispatch({
        product: {},
        type: PRODUCT_CHANGE,
      });
    }

    dispatch({
      incomeType: getIncomeTypeFromScale(scale),
      type: INCOME_TYPE_CHANGE,
    });

    // Change the scale regardless.
    return dispatch({
      scale,
      type: SCALE_CHANGE,
    });
  };
}

function changeState(state: StateValues) {
  return {
    state,
    type: STATE_CHANGE,
  };
}

function changeLastViewedProduct(productId: Customer['lastViewedProduct']) {
  return {
    lastViewedProduct: productId,
    type: LAST_VIEWED_PRODUCT_CHANGE,
  };
}

function changeProductType(productType: CustomerProductType) {
  return (dispatch: any, getState: any) => {
    const routerLocation = getState()?.router?.location?.pathname;

    const productTypeUrls = {
      [constants.PRODUCT_TYPE.COMBINED]: URLS.SALES.CHOOSE_YOUR_COVER_COMBINED_CHOOSE,
      [constants.PRODUCT_TYPE.PACKAGE]: URLS.SALES.CHOOSE_YOUR_COVER_COMBINED_CHOOSE,
      [constants.PRODUCT_TYPE.HOSPITAL]: URLS.SALES.PRODUCT_LISTING_HOSPITAL,
      [constants.PRODUCT_TYPE.EXTRAS]: URLS.SALES.PRODUCT_LISTING_EXTRAS,
    };

    const isProductListingPage = includes(routerLocation, values(productTypeUrls));

    if (isProductListingPage && productTypeUrls[productType as keyof typeof productTypeUrls] !== routerLocation) {
      dispatch(push(productTypeUrls[productType as keyof typeof productTypeUrls]));
    }

    return dispatch({
      productType,
      type: PRODUCT_TYPE_CHANGE,
    });
  };
}

function changeSwitching(switching: Customer['switching']) {
  return {
    switching,
    type: SWITCHING_CHANGE,
  };
}

const customerActions = {
  changeAddress,
  changeContinuousCover,
  changeDateOfBirth,
  changeFrequency,
  changeHasRefinedPrice,
  changeHasSetBasicDetails,
  changeHospitalExcess,
  changeIncome,
  changeIncomeTier,
  changeIncomeType,
  changeLastViewedProduct,
  changePaymentMethod,
  changeProductType,
  changeRebate,
  changeScale,
  changeState,
  changeSwitching,
};

export type CustomerActionType = {
  customer: typeof customerActions;
};

export default customerActions;
